import { Refresh } from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import WarningIcon from '@mui/icons-material/Warning';
import Header from 'src/components/nav-section/stepper-nav-bar/navbar'
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  Paper,
  Divider,
  Button,
  Container,
  Card,
  CardContent,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import api from 'src/Api';
import { logout } from 'src/actions/auth';
import RouterLink from 'src/hooks/RouterLink';
import ClientStepper from './ClientStepper';
import CandidatStepper from './CandidatStepper';
import Logo from 'src/components/Logo';

export default function VerifyAccount({ children }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cnxErr, setCnxErr] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [next, setNext] = useState(null);

  const showError = msg => {
    toast.error(msg, {
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };
  
  const showSuccess = msg => {
    toast.success(msg, {
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };
  
  const submit = () => {
    setSubmitting(true);
    api.post('/users/resend_email')
      .then(res => {
        setNext(res.data?.next);
        setSubmitting(false);
        if (res.data.sent) showSuccess('Email de vérification envoyé avec succès');
      })
      .catch(err => {
        setSubmitting(false);
        showError("Une erreur s'est produite lors de l'envoi de l'email.");
      });
  };
  
  const getUser = () => {
    setLoading(true);
    api.get('/users/get')
      .then(res => {
        setUser(res.data);
        setLoading(false);
      })
      .catch(err => {
        setCnxErr(true);
        setLoading(false);
      });
  };
  
  useEffect(() => {
    getUser();
  }, []);

  // Connection error state
  if (cnxErr) {
    return (
      <Container maxWidth="sm">
        <Card 
          elevation={3} 
          sx={{ 
            mt: 8, 
            p: 3, 
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          <WarningIcon sx={{ fontSize: 60, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>Problème de connexion</Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Nous n'avons pas pu récupérer vos informations. Veuillez vérifier votre connexion internet.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<Refresh />}
            onClick={() => getUser()}
            sx={{ mt: 2 }}
          >
            Réessayer
          </Button>
        </Card>
      </Container>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '80vh',
          }}
        >
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 3 }}>Chargement en cours...</Typography>
          <Typography variant="body2" color="text.secondary">
            Veuillez patienter pendant que nous récupérons vos informations
          </Typography>
        </Box>
      </Container>
    );
  }

  // Email verification needed
  if (!user?.valid_email) {
    return (
      <Container maxWidth="md">
        <Card 
          elevation={3} 
          sx={{ 
            mt: { xs: 2, md: 4 }, 
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Box 
            sx={{ 
              p: { xs: 2, md: 4 },
              backgroundColor:  theme.palette.primary.main,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >

<Box sx={{ width: 180, height: '100%' }}>
			{/* <img src="/statics/logo_text.png" /> */}
			<img alt='logo' src={'/statics/logo_white.png'} />
		</Box>
            <Button
              variant="outlined"
              color="inherit"
              sx={{ 
                textTransform: 'none', 
                color: 'white',
                borderColor: 'white',
                '&:hover': { 
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              onClick={() => dispatch(logout())}
            >
              Se déconnecter
            </Button>
          </Box>
          
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                gap: 4
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" gutterBottom>
                  Bonjour {user?.fname},
                </Typography>
                
                <Alert 
                  severity="info" 
                  icon={<EmailIcon />}
                  sx={{ mb: 3 }}
                >
                  Veuillez vérifier votre adresse email
                </Alert>

                <Typography variant="body1" paragraph>
                  L'adresse <strong>{user?.email}</strong> n'est pas encore validée. Nous vous avons envoyé un lien de validation.
                </Typography>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  Veuillez vérifier votre boîte de réception et votre dossier spam.
                </Typography>

                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 1,
                    mt: 3
                  }}
                >
                  <Typography variant="body2">
                    Si vous n'avez pas reçu l'email, vous pouvez demander un nouvel envoi:
                  </Typography>
                  <CountdownTimer targetDate={next} submit={submit} submitting={submitting} />
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 4 }}>
                  <Button
                    to="/dashboard"
                    size="large"
                    variant="contained"
                    component={RouterLink}
                  >
                    Accéder à la page d'accueil
                  </Button>
                </Box>
              </Box>
              
              {!isMobile && (
                <Box 
                  sx={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flex: 1
                  }}
                >
                  <img
                    src="/statics/undraw_personal_email_re_4lx7.svg"
                    alt="Vérification email"
                    style={{ maxWidth: '100%', maxHeight: 250 }}
                  />
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
        <ToastContainer position="top-center" />
      </Container>
    );
  }

  // Account suspended
  if (!user?.active) {
    return (
      <Container maxWidth="md">
        <Card 
          elevation={3} 
          sx={{ 
            mt: { xs: 2, md: 4 }, 
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Box 
            sx={{ 
              p: { xs: 2, md: 3 },
              backgroundColor: theme.palette.error.main,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            {/* <Logo sx={{ height: '100%', width: 180 }} /> */}
            <Box sx={{ width: 180, height: '100%' }}>
			{/* <img src="/statics/logo_text.png" /> */}
			<img alt='logo' src={'/statics/logo_white.png'} />
		</Box>
            <Button
              variant="outlined"
              color="inherit"
              sx={{ 
                textTransform: 'none', 
                color: 'white',
                borderColor: 'white',
                '&:hover': { 
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              onClick={() => dispatch(logout())}
            >
              Se déconnecter
            </Button>
          </Box>
          
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                gap: 4
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" gutterBottom>
                  Bonjour {user?.name},
                </Typography>
                
                <Alert 
                  severity="error" 
                  sx={{ mb: 3 }}
                >
                  Votre compte a été suspendu
                </Alert>

                <Typography variant="body1" paragraph>
                  {user?.disactive_cause === 0 
                    ? "Votre compte a été suspendu. Veuillez contacter l'administrateur pour obtenir plus d'informations."
                    : "Votre compte a été suspendu en raison d'un nombre excessif d'annulations de missions. Veuillez contacter l'administrateur pour obtenir plus d'informations."}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 4 }}>
                  <Button
                    to="/dashboard"
                    size="large"
                    variant="contained"
                    color="primary"
                    component={RouterLink}
                  >
                    Accéder à la page d'accueil
                  </Button>
                </Box>
              </Box>
              
              {!isMobile && (
                <Box 
                  sx={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flex: 1
                  }}
                >
                  <img
                    src="/statics/undraw_personal_email_re_4lx7.svg"
                    alt="Compte suspendu"
                    style={{ maxWidth: '100%', maxHeight: 250 }}
                  />
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Step verification
  if (!user?.unverified_steps?.includes(-1)) return children;
  
  return (
<>
  <Header />
  <Box sx={{ pt: '80px' }}>
    {user?.role === 1 && <ClientStepper user={user} setUser={setUser} />}
    {user?.role === 2 && <CandidatStepper user={user} setUser={setUser} />}
  </Box>
  <ToastContainer position="top-center" />
</>
  );
}

const CountdownTimer = ({ targetDate, submit, submitting }) => {
  const [timeLeft, setTimeLeft] = useState({});

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = +new Date(targetDate) - +new Date();
      
      if (difference > 0) {
        return {
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        };
      } else {
        return null;
      }
    };

    const updateTimer = () => {
      setTimeLeft(calculateTimeLeft());
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  const disabled = timeLeft && (timeLeft.minutes > 0 || timeLeft.seconds > 0);
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Button
        variant="contained"
        color="primary"
        size="small"
        disabled={disabled || submitting}
        onClick={submit}
        sx={{ 
          minWidth: 120,
          textTransform: 'none'
        }}
      >
        {submitting ? (
          <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
        ) : null}
        Renvoyer l'email
      </Button>
      
      {disabled && (
        <Typography variant="body2" color="text.secondary">
          ({timeLeft.minutes}:{timeLeft.seconds < 10 ? `0${timeLeft.seconds}` : timeLeft.seconds})
        </Typography>
      )}
    </Box>
  );
};